import Guard from 'arg-guard'

import Casing from '@/helpers/casing'
import Renovation from './renovation'

/**
 * Defined structure of a property.
 * @export Property
 * @class Property
 */
export default class Property {

    /**
     * Creates an instance of a Property.
     * @param {PropertyAsJson|Property|PropertySourceDataFromAddressId|PropertySourceDataFromId|{ id: number }} data Source data - From API or state.
     * @throws TypeError The data parameter is not provided or null.
     * @memberof Property
     */
    constructor(data) {
        Guard.throwIf(data, 'data')

        /**
         * @param {string} addressPart
         * @returns {string}
         */
        const getCleanAddress = function(addressPart) {
            return addressPart
                ? Casing.properCase(addressPart
                    .replace(/\s\s+/g, ' ')
                    .replace(/,,/g, ',')
                    .replace(/,(\S)/g, ', $1')
                    .replace(/, $/, '')
                    .replace(/ ,$/, '')
                    .replace(/,$/, '')
                    .replace(/\s+$/, '')
                    .replace(/^ ,/, '')
                    .replace(/^, /, '')
                    .replace(/^,/, '')
                    .replace(/^\s+/, '')
                    .replace(/^(\d+),/, '$1')
                )
                : ''
        }

        const addressId = parseInt(data.id || data.addressId)

        // noinspection JSUnresolvedReference
        const locationId = data.location
            ? data.location
            : data.locationId ? data.locationId : null

        /**
         * @type {number}
         */
        this.id = !isNaN(addressId) ? addressId : 0

        /**
         * @type {string}
         */
        this.address = data.address || getCleanAddress(data.fullAddressNoPostcode)

        /**
         * @type {string}
         */
        this.postcode = data.postcode || ''

        /**
         * @type {boolean}
         */
        this.calculated = data.calculated || false

        /**
         * @type {?number}
         */
        this.location = locationId ? parseInt(locationId) : null
        this.key = this.id || this.location

        if (data.renovations) {
            /**
             * @type {Array<Renovation>|undefined}
             */
            this.renovations = data.renovations.map(r => Renovation.ensure(r, addressId))
        }
    }

    /**
     * Returns the property address in array parts.
     * @readonly
     * @returns {Array<string>}
     */
    get addressArray() {
        return this.address ? this.asString().split(', ') : [ this.postcode ]
    }

    /**
     * Appends renovation data to the property.
     * @param {Renovation|RenovationSourceData} renovation
     */
    appendRenovation(renovation) {
        renovation.addressId = this.id
        if (!this.renovations || !this.renovations.length) {
            this.renovations = [ Renovation.ensure(renovation) ]
        } else if (!this.renovations.filter(r => r.name === renovation.name)) {
            this.renovations.push(Renovation.ensure(renovation))
        } else {
            this.renovations = this.renovations.filter(r => r.name !== renovation.name)
            this.renovations.push(Renovation.ensure(renovation))
        }
    }

    /**
     * Returns the property address in lines.
     * @returns {string}
     */
    asLines() {
        return this.asString().replace(/, /g, '\n')
    }

    /**
     * Returns the property address delimited by commas.
     * @returns {string}
     */
    asString() {
        return this.address
            ? `${ this.address }, ${ this.postcode }`
            : this.postcode ? this.postcode : 'Unknown Address'
    }

    /**
     * Returns a short form of the property address delimited by commas.
     * @returns {string}
     */
    asShortString() {
        return this.addressArray.length < 4
            ? this.addressArray.join(', ')
            : `${ this.addressArray[0] }, ${ this.addressArray[1] }, ${ this.postcode }`
    }

    /**
     * Indicates if the property is served.
     * @returns {boolean}
     */
    isServedByScheme() {
        return this.id > 0
    }

    /**
     * Returns the object in a form suitable to store.
     * @returns {PropertyAsJson}
     */
    toStoreJson() {
        return this.renovations ? {
            id: this.id,
            address: this.address,
            postcode: this.postcode,
            location: this.location,
            calculated: this.calculated,
            renovations: this.renovations.map(r => r.toStoreJson()),
        } : {
            id: this.id,
            address: this.address,
            postcode: this.postcode,
            location: this.location,
            calculated: this.calculated,
        }
    }

    /**
     * Ensures that the provided data is already of this type. If not, it runs the constructor.
     * @static
     * @param {?PropertyAsJson|?Property|?PropertySourceDataFromAddressId|?PropertySourceDataFromId} data Optional source data - From API or state.* @returns {Property} Either the data is passed back if already of this type or the result of the constructor is.
     * @returns {Property}
     */
    static ensure(data) {
        return data && data instanceof Property ? data : new Property(data)
    }

    /**
     * Returns the object type from raw stored JSON data.
     * @static
     * @param {?PropertyAsJson} data The stored data.
     * @returns {?Property}
     */
    static fromStoreJson(data = null) {
        return data ? new Property(data) : null
    }
}
